import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Phone, MessageCircle, Wheat, LayoutDashboard, Mail, Calendar, ClipboardList, Package, RefreshCw } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { CONTACT } from "../lib/contact";

const STATUS_META = {
  new:        { label: "Yeni",            cls: "bg-amber-100 text-amber-800 border-amber-300" },
  contacted:  { label: "İletişime Geçildi",cls: "bg-blue-100 text-blue-800 border-blue-300" },
  confirmed:  { label: "Onaylandı",       cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  completed:  { label: "Tamamlandı",      cls: "bg-stone-200 text-stone-800 border-stone-300" },
  cancelled:  { label: "İptal",           cls: "bg-rose-100 text-rose-800 border-rose-300" },
};

const TRAINING_TYPE_LABEL = {
  face_to_face: "Yüz Yüze",
  online: "Online",
  not_sure: "Emin değil",
  business: "İşletme danışmanlığı",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("trainings");
  const [stats, setStats] = useState(null);
  const [trainings, setTrainings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const adminEmail = localStorage.getItem("em_admin_email");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t, o] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/training-applications"),
        api.get("/admin/bread-orders"),
      ]);
      setStats(s.data);
      setTrainings(t.data);
      setOrders(o.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("em_token");
        navigate("/admin/login");
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const logout = () => {
    localStorage.removeItem("em_token");
    localStorage.removeItem("em_admin_email");
    navigate("/admin/login");
  };

  const updateTrainingStatus = async (id, status) => {
    try {
      await api.patch(`/admin/training-applications/${id}`, { status });
      setTrainings((arr) => arr.map((a) => (a.id === id ? { ...a, status } : a)));
      toast.success("Durum güncellendi.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updateOrderStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bread-orders/${id}`, { status });
      setOrders((arr) => arr.map((a) => (a.id === id ? { ...a, status } : a)));
      toast.success("Durum güncellendi.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-10 py-10 lg:py-14" data-testid="page-admin-dashboard">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
        <div>
          <div className="eyebrow">Yönetici Paneli</div>
          <h1 className="font-serif mt-3 text-3xl lg:text-4xl font-semibold flex items-center gap-3">
            <LayoutDashboard size={28} style={{ color: "var(--em-terracotta)" }} />
            Dashboard
          </h1>
          {adminEmail && <div className="mt-1 text-sm text-[color:var(--em-text-mute)]">{adminEmail}</div>}
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[color:var(--em-border)] text-sm font-semibold hover:bg-[color:var(--em-stone)] transition-colors" data-testid="admin-refresh-btn">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Yenile
          </button>
          <button onClick={logout} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[color:var(--em-espresso)] text-sm font-semibold hover:bg-[color:var(--em-espresso)] hover:text-white transition-colors" data-testid="admin-logout-btn">
            <LogOut size={14} /> Çıkış
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={ClipboardList} label="Toplam Başvuru" value={stats?.training_total ?? "—"} testId="stat-training-total" />
        <StatCard icon={Mail} label="Yeni Başvuru" value={stats?.training_new ?? "—"} accent testId="stat-training-new" />
        <StatCard icon={Package} label="Toplam Sipariş" value={stats?.orders_total ?? "—"} testId="stat-orders-total" />
        <StatCard icon={Wheat} label="Yeni Sipariş" value={stats?.orders_new ?? "—"} accent testId="stat-orders-new" />
      </div>

      {/* Tabs */}
      <div className="mt-10 border-b flex gap-2" style={{ borderColor: "var(--em-border)" }}>
        {[
          { id: "trainings", label: `Eğitim Başvuruları (${trainings.length})` },
          { id: "orders", label: `Ekmek Siparişleri (${orders.length})` },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`admin-tab-${t.id}`}
            className={`px-5 py-3 text-sm font-semibold transition-colors ${
              tab === t.id ? "border-b-2 text-[color:var(--em-terracotta-hover)]" : "text-[color:var(--em-text-mute)] hover:text-[color:var(--em-espresso)]"
            }`}
            style={tab === t.id ? { borderBottomColor: "var(--em-terracotta)" } : {}}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="mt-6">
        {tab === "trainings" && (
          <TableWrap empty={trainings.length === 0 ? "Henüz eğitim başvurusu yok." : null}>
            {trainings.map((a) => (
              <ApplicationRow key={a.id} a={a} onStatusChange={updateTrainingStatus} />
            ))}
          </TableWrap>
        )}
        {tab === "orders" && (
          <TableWrap empty={orders.length === 0 ? "Henüz sipariş talebi yok." : null}>
            {orders.map((o) => (
              <OrderRow key={o.id} o={o} onStatusChange={updateOrderStatus} />
            ))}
          </TableWrap>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className="rounded-sm border p-5" style={{ borderColor: "var(--em-border)", background: accent ? "var(--em-terracotta-light)" : "var(--em-stone)" }} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-xs tracking-wide uppercase font-semibold text-[color:var(--em-espresso-soft)]">{label}</div>
        <Icon size={16} style={{ color: accent ? "var(--em-terracotta-hover)" : "var(--em-text-mute)" }} />
      </div>
      <div className="font-serif text-3xl font-semibold mt-2">{value}</div>
    </div>
  );
}

function TableWrap({ children, empty }) {
  if (empty) {
    return <div className="rounded-sm border p-10 text-center text-sm text-[color:var(--em-text-mute)]" style={{ borderColor: "var(--em-border)", background: "var(--em-stone)" }} data-testid="admin-empty-state">{empty}</div>;
  }
  return <div className="grid gap-3">{children}</div>;
}

function StatusSelect({ status, onChange, testId }) {
  return (
    <select
      value={status}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className={`text-xs font-semibold px-3 py-1.5 rounded-full border cursor-pointer ${STATUS_META[status]?.cls || "border-stone-300"}`}
    >
      {Object.entries(STATUS_META).map(([k, v]) => (
        <option key={k} value={k}>{v.label}</option>
      ))}
    </select>
  );
}

function formatDate(iso) {
  try { return new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

function ApplicationRow({ a, onStatusChange }) {
  return (
    <div className="rounded-sm border p-5 lg:p-6 grid lg:grid-cols-12 gap-4" style={{ borderColor: "var(--em-border)", background: "white" }} data-testid={`training-row-${a.id}`}>
      <div className="lg:col-span-4">
        <div className="font-serif text-lg font-semibold">{a.full_name}</div>
        <div className="mt-1 text-xs text-[color:var(--em-text-mute)]"><Calendar size={11} className="inline mr-1" />{formatDate(a.created_at)}</div>
        <div className="mt-3 flex flex-wrap gap-3 text-xs">
          <a href={`tel:${a.phone}`} className="inline-flex items-center gap-1.5 text-[color:var(--em-espresso)] hover:text-[color:var(--em-terracotta-hover)]"><Phone size={12} /> {a.phone}</a>
          <a href={`https://wa.me/${a.phone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[color:var(--em-espresso)] hover:text-[color:var(--em-terracotta-hover)]"><MessageCircle size={12} /> WhatsApp</a>
          <a href={`mailto:${a.email}`} className="inline-flex items-center gap-1.5 text-[color:var(--em-espresso)] hover:text-[color:var(--em-terracotta-hover)]"><Mail size={12} /> {a.email}</a>
        </div>
      </div>
      <div className="lg:col-span-6 text-sm space-y-1.5">
        <div><strong>Eğitim:</strong> {TRAINING_TYPE_LABEL[a.training_type] || a.training_type}</div>
        <div><strong>Deneyim:</strong> <span className="text-[color:var(--em-espresso-soft)]">{a.experience}</span></div>
        <div><strong>Hedef:</strong> <span className="text-[color:var(--em-espresso-soft)]">{a.goal}</span></div>
        {a.message && <div><strong>Not:</strong> <span className="text-[color:var(--em-espresso-soft)]">{a.message}</span></div>}
      </div>
      <div className="lg:col-span-2 flex lg:flex-col lg:items-end justify-end gap-2">
        <StatusSelect status={a.status} onChange={(s) => onStatusChange(a.id, s)} testId={`training-status-${a.id}`} />
      </div>
    </div>
  );
}

function OrderRow({ o, onStatusChange }) {
  return (
    <div className="rounded-sm border p-5 lg:p-6 grid lg:grid-cols-12 gap-4" style={{ borderColor: "var(--em-border)", background: "white" }} data-testid={`order-row-${o.id}`}>
      <div className="lg:col-span-4">
        <div className="font-serif text-lg font-semibold">{o.full_name}</div>
        <div className="mt-1 text-xs text-[color:var(--em-text-mute)]"><Calendar size={11} className="inline mr-1" />{formatDate(o.created_at)}</div>
        <div className="mt-3 flex flex-wrap gap-3 text-xs">
          <a href={`tel:${o.phone}`} className="inline-flex items-center gap-1.5 text-[color:var(--em-espresso)] hover:text-[color:var(--em-terracotta-hover)]"><Phone size={12} /> {o.phone}</a>
          <a href={`https://wa.me/${o.phone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[color:var(--em-espresso)] hover:text-[color:var(--em-terracotta-hover)]"><MessageCircle size={12} /> WhatsApp</a>
        </div>
      </div>
      <div className="lg:col-span-6 text-sm space-y-1.5">
        <div><strong>Ürün:</strong> {o.product}</div>
        <div><strong>Adet:</strong> {o.quantity}</div>
        <div><strong>Teslim Tarihi:</strong> {o.pickup_date}</div>
        {o.note && <div><strong>Not:</strong> <span className="text-[color:var(--em-espresso-soft)]">{o.note}</span></div>}
      </div>
      <div className="lg:col-span-2 flex lg:flex-col lg:items-end justify-end gap-2">
        <StatusSelect status={o.status} onChange={(s) => onStatusChange(o.id, s)} testId={`order-status-${o.id}`} />
      </div>
    </div>
  );
}
